import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  UsersIcon,
  CpuChipIcon,
  ClockIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Home: React.FC = () => {
  const features = [
    {
      icon: CpuChipIcon,
      title: 'AI-Powered GST Invoice Scanner',
      description: 'Advanced AI extracts GST details, GSTIN, HSN codes from invoices with 99.5% accuracy'
    },
    {
      icon: DocumentTextIcon,
      title: 'Automated GST Return Filing',
      description: 'Automatically generate GSTR-1, GSTR-3B returns from processed invoices'
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Client GST Document Portal',
      description: 'Clients upload GST invoices, bills directly through secure branded portals'
    },
    {
      icon: ChartBarIcon,
      title: 'GST Analytics & Reports',
      description: 'Real-time GST liability, input credit analysis and compliance reporting'
    },
    {
      icon: ClockIcon,
      title: 'Save 80% Time on GST Work',
      description: 'Eliminate manual GST data entry and focus on advisory services for clients'
    },
    {
      icon: ShieldCheckIcon,
      title: 'GST Compliance & Security',
      description: 'Bank-grade security with full GST audit trail and data protection compliance'
    }
  ];

  const benefits = [
    'Reduce GST processing time by 80%',
    'Eliminate manual GST data entry errors',
    'Faster GST return filing for clients',  
    'Scale CA practice without hiring GST staff',
    'Ensure GST compliance with automated audit trails',
    'Access GST data anywhere with cloud solution'
  ];

  const testimonials = [
    {
      name: 'Rajesh Sharma',
      position: 'Senior Partner, Sharma & Associates',
      content: 'AICaFriend transformed our GST practice. We now handle 3x more GST returns with the same team size.',
      rating: 5
    },
    {
      name: 'Priya Patel',
      position: 'CA, Patel Tax Consultants',
      content: 'The GST invoice scanning is incredibly accurate. It saves us 20+ hours per week on GST data entry.',
      rating: 5
    },
    {
      name: 'Amit Gupta',
      position: 'Managing Partner, Gupta & Co.',
      content: 'Our clients love the GST document upload portal. GST return filing is now completely seamless.',
      rating: 5
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-50 via-white to-purple-50 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Free GST Software for 
                <span className="gradient-text"> CA Firms</span>
                <br />AI-Powered GST Accounting Solution
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Free AI-powered GST invoice processing for chartered accountants. Automate GST returns, 
                invoice scanning & client management. Trusted by 500+ CA firms across India.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/pricing"
                  className="btn-primary px-8 py-4 text-lg font-semibold rounded-lg text-white shadow-lg"
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/features"
                  className="btn-secondary px-8 py-4 text-lg font-semibold rounded-lg"
                >
                  Watch Demo
                </Link>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                ✨ No credit card required • 14-day free trial • Setup in 5 minutes
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features for Modern CA Firms
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to streamline invoice processing and grow your practice
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card-hover bg-white p-6 rounded-xl border border-gray-200"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose AICaFriend?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Transform your invoice processing workflow and deliver exceptional value to your clients
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-8 text-white">
                <div className="text-center">
                  <UsersIcon className="h-16 w-16 mx-auto mb-4 opacity-80" />
                  <h3 className="text-2xl font-bold mb-4">Join 500+ CA Firms</h3>
                  <p className="text-lg opacity-90 mb-6">
                    Already using AICaFriend to streamline their practice
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-3xl font-bold">99.5%</div>
                      <div className="text-sm opacity-80">Accuracy</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold">80%</div>
                      <div className="text-sm opacity-80">Time Saved</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold">500+</div>
                      <div className="text-sm opacity-80">Happy Clients</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What CA Firms Say About Us
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from real CA professionals
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl border border-gray-200 card-hover"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.position}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join hundreds of CA firms already using AICaFriend to automate their invoice processing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="bg-white text-indigo-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-indigo-600 transition-all duration-200"
            >
              Schedule Demo
            </Link>
          </div>
          <p className="mt-4 text-sm text-indigo-200">
            14-day free trial • No setup fees • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;